import { AppContext } from "@lightspeed-invoicing/core-client/frontend"
import { defineMessages } from "react-intl"
import { BulkActionDefinition, BulkActionType, ColumnDefinition, ColumnDefinitionCategory, DataType, TotalType } from "."
import { ObjectType, ObjectTypeConfig } from ".."
import { GlobalHelper } from "../../../../indexImports"
import { Permissions } from "../../../../role/Guard"
import { BULK_OPERATION_MESSAGES, BULK_OPERATION_TYPE } from "../../../../utils/automation/bulk"
import { PREFERENCE_TYPE } from "../../constants"

export enum CompaniesColumnIds {
    NAME = "organizationName",
    CUSTOMER_TYPE = "customerTypeName",
    CITY = "city",
    PHONE_NUMBER = "phoneNumber",
    CREATED_AT_DATE = "createdAt",
    UPDATED_AT_DATE = "updatedAt",
    TAX_NUMBER = "taxNumber",
    EMAIL = "email",
    DEFAULT_TAX = "taxId",
    RECURRING_STATEMENT_ENABLED = "recurringStatementEnabled",
    RECURRING_STATEMENT_NEXT_CREATION_DATE = "recurringStatementNextCreationDate",
    RECURRING_STATEMENT_SEND_EMAIL_ENABLED = "recurringStatementSendEmail",
    RECURRING_STATEMENT_AUTO_CAPTURE_PAYMENT = "recurringStatementCapturePayment",
    RECURRING_STATEMENT_WITH_ATTACHED_PDF = "recurringStatementAttachPdf",
    ARCHIVED = "isArchived",
}

const columnDefinitions: ColumnDefinition[] = [
    {
        id: CompaniesColumnIds.NAME,
        sortId: "organizationNameKeyword",
        defaultSort: true,
        dataType: DataType.LINK,
        dataAttributes: {
            redirect: "/contacts/{id}/details"
        },
        totalAttributes: {
            key: "totalItemsCount",
            type: TotalType.STRING,
        },
        sortable: true,
        identifier: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: CompaniesColumnIds.CUSTOMER_TYPE,
        dataType: DataType.STRING,
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: CompaniesColumnIds.CITY,
        accessorFn: row => {
            const address = {
                city: row.city,
                stateCode: row.stateCode,
                state: row.state,
                country: row.country,
                countryCode: row.countryCode,
            }
            return GlobalHelper.formatCityFromAddress(address, AppContext.getLocale().getLanguageWithoutRegionCode())
        },
        sortId: "cityKeyword",
        dataType: DataType.STRING,
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: CompaniesColumnIds.PHONE_NUMBER,
        accessorFn: row => {
            if (row.phoneNumber?.length) {
                return row.phoneNumber[0]
            }
            return ""
        },
        sortId: "phoneNumberKeyword",
        dataType: DataType.STRING,
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: CompaniesColumnIds.CREATED_AT_DATE,
        dataType: DataType.DATE,
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: CompaniesColumnIds.UPDATED_AT_DATE,
        dataType: DataType.DATE,
        sortable: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: CompaniesColumnIds.TAX_NUMBER,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: CompaniesColumnIds.EMAIL,
        sortId: "emailKeyword",
        dataType: DataType.ARRAY,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: CompaniesColumnIds.DEFAULT_TAX,
        dataType: DataType.OPTIONS,
        dataAttributes: {
            optionsKey: "taxOptions",
            loadingKey: "loadingTaxOptions",
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: CompaniesColumnIds.RECURRING_STATEMENT_ENABLED,
        dataType: DataType.BOOLEAN,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: CompaniesColumnIds.RECURRING_STATEMENT_NEXT_CREATION_DATE,
        dataType: DataType.DATE,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: CompaniesColumnIds.RECURRING_STATEMENT_SEND_EMAIL_ENABLED,
        dataType: DataType.BOOLEAN,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: CompaniesColumnIds.RECURRING_STATEMENT_AUTO_CAPTURE_PAYMENT,
        dataType: DataType.BOOLEAN,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: CompaniesColumnIds.RECURRING_STATEMENT_WITH_ATTACHED_PDF,
        dataType: DataType.BOOLEAN,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: CompaniesColumnIds.ARCHIVED,
        dataType: DataType.BOOLEAN,
        sortable: true,
        categories: [ColumnDefinitionCategory.OTHER],
    },
]

const headerMessages = defineMessages({
    [CompaniesColumnIds.NAME]: {
        id: "generic-listing.companies.table-header.name",
        defaultMessage: "Name",
    },
    [CompaniesColumnIds.CUSTOMER_TYPE]: {
        id: "generic-listing.companies.table-header.customer-type",
        defaultMessage: "Customer type",
    },
    [CompaniesColumnIds.CITY]: {
        id: "generic-listing.companies.table-header.city",
        defaultMessage: "City",
    },
    [CompaniesColumnIds.PHONE_NUMBER]: {
        id: "generic-listing.companies.table-header.phone-number",
        defaultMessage: "Phone number",
    },
    [CompaniesColumnIds.CREATED_AT_DATE]: {
        id: "generic-listing.companies.table-header.created-at-date",
        defaultMessage: "Created at",
    },
    [CompaniesColumnIds.UPDATED_AT_DATE]: {
        id: "generic-listing.companies.table-header.update-at-date",
        defaultMessage: "Updated at",
    },
    [CompaniesColumnIds.TAX_NUMBER]: {
        id: "generic-listing.companies.table-header.tax-number",
        defaultMessage: "Tax Number",
    },
    [CompaniesColumnIds.EMAIL]: {
        id: "generic-listing.companies.table-header.email",
        defaultMessage: "Email",
    },
    [CompaniesColumnIds.DEFAULT_TAX]: {
        id: "generic-listing.companies.table-header.default-tax",
        defaultMessage: "Default Tax",
    },
    [CompaniesColumnIds.RECURRING_STATEMENT_ENABLED]: {
        id: "generic-listing.companies.table-header.recurring-statement-enabled",
        defaultMessage: "Recurring Statement Enabled",
    },
    [CompaniesColumnIds.RECURRING_STATEMENT_NEXT_CREATION_DATE]: {
        id: "generic-listing.companies.table-header.recurring-statement-next-creation-date",
        defaultMessage: "Recurring Statement Next Creation Date",
    },
    [CompaniesColumnIds.RECURRING_STATEMENT_SEND_EMAIL_ENABLED]: {
        id: "generic-listing.companies.table-header.recurring-statement-send-email-enabled",
        defaultMessage: "Recurring Statement Send Email Enabled",
    },
    [CompaniesColumnIds.RECURRING_STATEMENT_AUTO_CAPTURE_PAYMENT]: {
        id: "generic-listing.companies.table-header.recurring-statement-auto-capture-payment",
        defaultMessage: "Recurring Statement Auto Capture Payment",
    },
    [CompaniesColumnIds.RECURRING_STATEMENT_WITH_ATTACHED_PDF]: {
        id: "generic-listing.companies.table-header.recurring-statement-with-attached-pdf",
        defaultMessage: "Recurring Statement With Attached PDF",
    },
    [CompaniesColumnIds.ARCHIVED]: {
        id: "generic-listing.companies.table-header.archived",
        defaultMessage: "Archived",
    },
})

const bulkActionDefinitions: BulkActionDefinition[] = [
    {
        id: BULK_OPERATION_TYPE.CREATE_ACCOUNT_STATEMENT,
        message: BULK_OPERATION_MESSAGES[BULK_OPERATION_TYPE.CREATE_ACCOUNT_STATEMENT],
        icon: "file-invoice",
    },
    {
        id: BULK_OPERATION_TYPE.CONTACT_UPDATE,
        message: BULK_OPERATION_MESSAGES[BULK_OPERATION_TYPE.CONTACT_UPDATE],
        icon: "edit",
    },
    {
        id: BULK_OPERATION_TYPE.DELETE,
        message: BULK_OPERATION_MESSAGES[BULK_OPERATION_TYPE.DELETE],
        icon: "trash",
        type: BulkActionType.DANGER,
        guard: Permissions.DeleteContact
    },
]

const configuration: ObjectTypeConfig = {
    objectType: ObjectType.COMPANIES,
    columnDefinitions,
    headerMessages,
    listingPreferencesKey: PREFERENCE_TYPE.LISTING_COMPANIES,
    searchPlaceholder: {
        id: "generic-listing.companies.search-placeholder",
        defaultMessage: "Search companies...",
    },
    displayString: {
        id: "generic-listing.companies.display-string",
        defaultMessage: "Companies",
    },
    searchObjectType: ObjectType.CONTACTS,
    bulkActionDefinitions,
    additionalFilters: {
        contactType: ["organizations"]
    },
    exportModalComponent: require("../../../../common/listing/generic-listing/export-modals/GenericExportModal"),
}

export default configuration


// WEBPACK FOOTER //
// ./src/state/listing/generic-listing/column-definitions/companies.ts